/**
 * 체험단 신청 폼 컴포넌트
 *
 * 캠페인 신청에 필요한 전체 폼: 기본 정보(이름, 전화번호, 이메일),
 * 성별, 생년월일, 배송지(Kakao 우편번호 연동), SNS 계정,
 * 신청 사유, 추가 질문, 개인정보 동의 등을 포함한다.
 *
 * field_config에 따라 필드를 조건부 표시하고,
 * Meta Pixel + CAPI Lead 이벤트를 발행한다.
 */

import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { trackPixelEvent } from '@/lib/metaPixel'
import { sendCapiEvent } from '@/lib/metaCapi'
import { hashPhone, hashEmail, hashText, parseKoreanName } from '@/lib/metaHash'
import type {
  PublicCampaignResponse,
  SeedingApplicationCreateRequest,
} from '@/lib/seedingPublicApi'
import { seedingPublicApi } from '@/lib/seedingPublicApi'
import { SNS_CHANNELS, normalizeSnsAccount, validateSnsAccount } from '@/lib/snsUtils'
import { formatPhoneInput, stripPhone } from '@/lib/phoneUtils'
import { getCookie } from './utils'

// ==================== Kakao 우편번호 서비스 ====================

// Kakao 우편번호 서비스 타입 (global 선언은 라우트 파일에 유지)
declare global {
  interface KakaoPostcodeResult {
    zonecode: string
    address: string
    roadAddress: string
    jibunAddress: string
    addressType: 'R' | 'J'
    buildingName: string
    apartment: 'Y' | 'N'
  }
  interface Window {
    kakao: {
      Postcode: new (options: {
        oncomplete: (data: KakaoPostcodeResult) => void
        width?: string | number
        height?: string | number
      }) => {
        open: () => void
        embed: (element: HTMLElement) => void
      }
    }
  }
}

/** Kakao 우편번호 서비스 스크립트 동적 로딩 (2026년 kakao 도메인 전환) */
const KAKAO_POSTCODE_URL = 'https://t1.kakaocdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'

function loadKakaoPostcode(): Promise<void> {
  if (window.kakao?.Postcode) return Promise.resolve()

  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${KAKAO_POSTCODE_URL}"]`)) {
      const check = setInterval(() => {
        if (window.kakao?.Postcode) { clearInterval(check); resolve() }
      }, 100)
      return
    }
    const script = document.createElement('script')
    script.src = KAKAO_POSTCODE_URL
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('우편번호 서비스를 불러올 수 없어요.'))
    document.head.appendChild(script)
  })
}

// ==================== 생년월일 유틸 ====================

const BIRTHDATE_TEMPLATE = 'YYYY.MM.DD'
const BIRTHDATE_DIGIT_POSITIONS = [0, 1, 2, 3, 5, 6, 8, 9]

/** 입력된 숫자를 "YYYY.MM.DD" 템플릿에 맞춰 포맷 */
function formatBirthdateWithTemplate(digits: string) {
  let result = ''
  let dIdx = 0
  for (let i = 0; i < BIRTHDATE_TEMPLATE.length; i++) {
    if (BIRTHDATE_DIGIT_POSITIONS.includes(i)) {
      result += dIdx < digits.length ? digits[dIdx] : BIRTHDATE_TEMPLATE[i]
      dIdx++
    } else {
      result += BIRTHDATE_TEMPLATE[i]
    }
  }
  return result
}

/** 입력된 부분까지의 길이 (오버레이 스타일링용) */
function getBirthdateTypedLength(digits: string) {
  if (!digits.length) return 0
  const lastDigitTemplatePos = BIRTHDATE_DIGIT_POSITIONS[digits.length - 1]
  return lastDigitTemplatePos + 1
}

// ==================== 채널별 팔로워 범위 ====================

const followerRangesByChannel: Record<string, { value: string; label: string }[]> = {
  blog: [
    { value: '', label: '선택' },
    { value: 'under_100', label: '100 미만' },
    { value: '100_500', label: '100 ~ 500' },
    { value: '500_1k', label: '500 ~ 1,000' },
    { value: '1k_3k', label: '1,000 ~ 3,000' },
    { value: '3k_5k', label: '3,000 ~ 5,000' },
    { value: '5k_10k', label: '5,000 ~ 1만' },
    { value: 'over_10k', label: '1만 이상' },
  ],
  instagram: [
    { value: '', label: '선택' },
    { value: 'under_1k', label: '1,000 미만' },
    { value: '1k_3k', label: '1,000 ~ 3,000' },
    { value: '3k_5k', label: '3,000 ~ 5,000' },
    { value: '5k_10k', label: '5,000 ~ 1만' },
    { value: '10k_30k', label: '1만 ~ 3만' },
    { value: '30k_100k', label: '3만 ~ 10만' },
    { value: 'over_100k', label: '10만 이상' },
  ],
  youtube: [
    { value: '', label: '선택' },
    { value: 'under_500', label: '500 미만' },
    { value: '500_1k', label: '500 ~ 1,000' },
    { value: '1k_3k', label: '1,000 ~ 3,000' },
    { value: '3k_10k', label: '3,000 ~ 1만' },
    { value: '10k_50k', label: '1만 ~ 5만' },
    { value: '50k_100k', label: '5만 ~ 10만' },
    { value: 'over_100k', label: '10만 이상' },
  ],
}
// 틱톡은 인스타와 동일
followerRangesByChannel.tiktok = followerRangesByChannel.instagram

/** SNS 채널 레이블 */
const channelLabels: Record<string, { name: string; accountLabel: string; followersLabel: string }> = {
  blog: { name: '네이버 블로그', accountLabel: '블로그 URL', followersLabel: '일 방문자' },
  instagram: { name: '인스타그램', accountLabel: '계정', followersLabel: '팔로워' },
  tiktok: { name: '틱톡', accountLabel: '계정', followersLabel: '팔로워' },
  youtube: { name: '유튜브', accountLabel: '채널', followersLabel: '구독자' },
}

// ==================== 컴포넌트 ====================

interface ApplicationFormProps {
  campaign: PublicCampaignResponse
  mallId: string
  shopNo: number
  campaignId: string
  onSuccess: () => void
  previewMode?: boolean   // 미리보기: 입력 허용, 제출/인증 버튼만 비활성화
  forceMobile?: boolean   // 미리보기 모바일: md: 브레이크포인트 제거
}

export function ApplicationForm({
  campaign,
  mallId,
  shopNo,
  campaignId,
  onSuccess,
  previewMode,
  forceMobile,
}: ApplicationFormProps) {
  const [formData, setFormData] = useState<SeedingApplicationCreateRequest>({
    name: '',
    phone: '',
    email: '',
    gender: undefined,
    birthdate: undefined,
    zipcode: '',
    address1: '',
    address2: '',
    sns_accounts: [],
    application_reason: '',
    extra_question_answers: [],
    agreed_privacy: false,
    agreed_third_party: false,
  })
  const [isApplying, setIsApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 상세주소 input ref (주소 검색 후 포커스 이동용)
  const address2Ref = useRef<HTMLInputElement>(null)

  // Kakao 우편번호 모달 상태
  const [showPostcodeModal, setShowPostcodeModal] = useState(false)
  const postcodeEmbedRef = useRef<HTMLDivElement>(null)

  // 모달이 열리면 embed 렌더링
  useEffect(() => {
    if (!showPostcodeModal || !postcodeEmbedRef.current) return
    let cancelled = false

    loadKakaoPostcode().then(() => {
      if (cancelled || !postcodeEmbedRef.current) return
      new window.kakao.Postcode({
        oncomplete: (data: KakaoPostcodeResult) => {
          const addr = data.addressType === 'R' ? data.roadAddress : data.jibunAddress
          setFormData((prev) => ({
            ...prev,
            zipcode: data.zonecode,
            address1: addr,
          }))
          clearFieldError('address')
          setShowPostcodeModal(false)
          setTimeout(() => address2Ref.current?.focus(), 100)
        },
        width: '100%',
        height: '100%',
      }).embed(postcodeEmbedRef.current)
    }).catch((err: any) => {
      if (!cancelled) {
        setError(err.message || '우편번호 서비스를 불러올 수 없어요.')
        setShowPostcodeModal(false)
      }
    })

    return () => { cancelled = true }
  }, [showPostcodeModal])

  // 전화번호 인증
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [isSendingCode, setIsSendingCode] = useState(false)
  const [isVerifyingCode, setIsVerifyingCode] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  // SNS 입력 인라인 에러 (채널별)
  const [snsErrors, setSnsErrors] = useState<Record<string, string>>({})

  // 생년월일 표시용 (YYYY.MM.DD 포맷)
  const [birthdateDisplay, setBirthdateDisplay] = useState('')
  const [birthdateError, setBirthdateError] = useState<string | null>(null)

  // 필드별 인라인 에러
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // 개인정보 동의 약관 펼침
  const [showPrivacyDetail, setShowPrivacyDetail] = useState(false)
  const [showThirdPartyDetail, setShowThirdPartyDetail] = useState(false)

  // ==================== SNS 핸들러 ====================

  const updateSnsAccount = (channel: string, field: 'account' | 'followers', value: string) => {
    setFormData((prev) => {
      const accounts = [...(prev.sns_accounts || [])]
      const idx = accounts.findIndex((a) => a.channel === channel)
      if (idx >= 0) {
        accounts[idx] = { ...accounts[idx], [field]: value }
      } else {
        accounts.push({ channel, account: field === 'account' ? value : '', followers: field === 'followers' ? value : undefined })
      }
      return { ...prev, sns_accounts: accounts }
    })
  }

  const handleSnsBlur = (channel: string) => {
    const raw = formData.sns_accounts?.find((a) => a.channel === channel)?.account || ''
    if (!raw.trim()) {
      setSnsErrors((prev) => { const next = { ...prev }; delete next[channel]; return next })
      return
    }
    const normalized = normalizeSnsAccount(channel, raw)
    if (!validateSnsAccount(channel, normalized, raw)) {
      const chDef = SNS_CHANNELS[channel]
      setSnsErrors((prev) => ({ ...prev, [channel]: `${chDef?.name || channel} 계정 형식이 올바르지 않아요. (예: ${chDef?.placeholder || ''})` }))
    } else {
      setSnsErrors((prev) => { const next = { ...prev }; delete next[channel]; return next })
    }
  }

  const getSnsData = (channel: string) => {
    return formData.sns_accounts?.find((a) => a.channel === channel) || { channel, account: '', followers: undefined }
  }

  // ==================== 생년월일 핸들러 ====================

  const handleBirthdateChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 8)
    setBirthdateDisplay(digits)
    if (birthdateError) setBirthdateError(null)
    clearFieldError('birthdate')
  }

  const handleBirthdateBlur = () => {
    const digits = birthdateDisplay
    if (digits.length === 0) {
      setFormData((prev) => ({ ...prev, birthdate: undefined }))
      setBirthdateError(null)
      return
    }
    if (digits.length !== 8) {
      setBirthdateError('생년월일을 정확히 입력해주세요.')
      return
    }
    const y = +digits.slice(0, 4)
    const m = +digits.slice(4, 6)
    const d = +digits.slice(6, 8)
    const date = new Date(y, m - 1, d)
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
      setBirthdateError('유효하지 않은 날짜예요.')
      return
    }
    if (date > new Date()) {
      setBirthdateError('미래 날짜는 입력할 수 없어요.')
      return
    }
    setBirthdateError(null)
    setFormData((prev) => ({
      ...prev,
      birthdate: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
    }))
  }

  // ==================== 필드 에러 ====================

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  // ==================== 전체 폼 검증 ====================

  const validateForm = (): Record<string, string> | null => {
    const errors: Record<string, string> = {}

    if (!formData.name.trim()) errors.name = '이름을 입력해주세요.'
    if (!formData.phone.trim()) errors.phone = '전화번호를 입력해주세요.'
    if (formData.phone.trim() && !phoneVerified) errors.phone = '전화번호 인증이 필요해요.'
    if (campaign.field_config?.email === 'required' && !formData.email?.trim()) errors.email = '이메일을 입력해주세요.'
    if (campaign.field_config?.gender === 'required' && !formData.gender) errors.gender = '성별을 선택해주세요.'
    if (campaign.field_config?.birthdate === 'required' && !formData.birthdate) errors.birthdate = '생년월일을 입력해주세요.'
    if (birthdateError) errors.birthdate = birthdateError
    if (campaign.field_config?.address === 'required') {
      if (!formData.zipcode?.trim() || !formData.address1?.trim()) errors.address = '주소 찾기 버튼을 눌러 배송지를 입력해주세요.'
    }

    // SNS 계정 검증
    if (campaign.social_media_channels) {
      const channelNames: Record<string, string> = {
        blog: '네이버 블로그', instagram: '인스타그램', tiktok: '틱톡', youtube: '유튜브',
      }
      for (const [ch, visibility] of Object.entries(campaign.social_media_channels)) {
        const data = formData.sns_accounts?.find((a) => a.channel === ch)
        const raw = data?.account?.trim() || ''
        if (visibility === 'required' && !raw) {
          errors[`sns_${ch}`] = `${channelNames[ch] || ch} 계정을 입력해주세요.`
          setSnsErrors((prev) => ({ ...prev, [ch]: `${channelNames[ch] || ch} 계정은 필수 입력이에요.` }))
        } else if (raw) {
          const normalized = normalizeSnsAccount(ch, raw)
          if (!validateSnsAccount(ch, normalized, raw)) {
            const chDef = SNS_CHANNELS[ch]
            const msg = `${chDef?.name || ch} 계정 형식이 올바르지 않아요. (예: ${chDef?.placeholder || ''})`
            errors[`sns_${ch}`] = msg
            setSnsErrors((prev) => ({ ...prev, [ch]: msg }))
          }
        }
      }
    }

    if (campaign.field_config?.reason === 'required' && !formData.application_reason?.trim()) errors.reason = '신청 사유를 입력해주세요.'

    // 추가 질문 필수 검증
    for (const q of campaign.extra_questions || []) {
      if (q.required) {
        const answer = (formData.extra_question_answers || []).find(a => a.question_id === q.id)
        const val = answer?.value
        if (!val || (Array.isArray(val) && val.length === 0) || (typeof val === 'string' && !val.trim())) {
          errors[`custom_${q.id}`] = `${q.label}을(를) 입력해주세요.`
        }
      }
    }

    if (!formData.agreed_privacy) errors.agreed_privacy = '개인정보 수집·이용에 동의해주세요.'
    if (!formData.agreed_third_party) errors.agreed_third_party = '제3자 제공에 동의해주세요.'

    setFieldErrors(errors)

    if (Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0]
      const el = document.querySelector(`[data-field="${firstErrorKey}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return errors
    }

    return null
  }

  // ==================== OAuth prefill 로드 ====================

  useEffect(() => {
    const prefillStr = localStorage.getItem('seeding_prefill')
    if (prefillStr) {
      try {
        const prefill = JSON.parse(prefillStr)
        setFormData((prev) => ({
          ...prev,
          name: prefill.name || '',
          phone: prefill.phone || '',
          email: prefill.email || '',
          zipcode: prefill.zipcode || '',
          address1: prefill.address1 || '',
          address2: prefill.address2 || '',
          sns_accounts: prefill.sns_accounts || [],
        }))
      } catch (e) {
        console.error('prefill parse error:', e)
      }
    }
  }, [])

  // ==================== 인증코드 핸들러 ====================

  const handleSendCode = async () => {
    if (!formData.phone) {
      setVerifyError('전화번호를 입력해주세요.')
      return
    }
    setIsSendingCode(true)
    setVerifyError(null)
    try {
      await seedingPublicApi.sendPhoneCode(stripPhone(formData.phone))
      setCodeSent(true)
    } catch (err: any) {
      setVerifyError(err.message || '인증코드 발송에 실패했어요.')
    } finally {
      setIsSendingCode(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setVerifyError('인증코드를 입력해주세요.')
      return
    }
    setIsVerifyingCode(true)
    setVerifyError(null)
    try {
      await seedingPublicApi.verifyPhoneCode(stripPhone(formData.phone), verificationCode)
      setPhoneVerified(true)
    } catch (err: any) {
      setVerifyError(err.message || '인증코드가 올바르지 않아요.')
    } finally {
      setIsVerifyingCode(false)
    }
  }

  // ==================== 신청 처리 ====================

  const handleApply = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) return

    setIsApplying(true)
    setError(null)

    try {
      // Meta Lead 이벤트 (브라우저 Pixel + CAPI)
      const eventId = campaign.meta_pixel_id ? crypto.randomUUID() : undefined
      if (campaign.meta_pixel_id && eventId) {
        trackPixelEvent(
          campaign.meta_pixel_id,
          'Lead',
          { content_name: campaign.title, content_ids: [campaignId] },
          eventId,
        )
        const { firstName, lastName } = parseKoreanName(formData.name)
        sendCapiEvent({
          mallId,
          shopNo,
          eventName: 'Lead',
          eventId,
          eventSourceUrl: window.location.href,
          userData: {
            ph: await hashPhone(stripPhone(formData.phone)),
            em: formData.email ? await hashEmail(formData.email) : undefined,
            fn: firstName ? await hashText(firstName) : undefined,
            ln: lastName ? await hashText(lastName) : undefined,
            zp: formData.zipcode ? await hashText(formData.zipcode) : undefined,
            ct: formData.address1 ? await hashText(formData.address1.split(' ')[0]) : undefined,
            country: await hashText('kr'),
            fbp: getCookie('_fbp'),
            fbc: getCookie('_fbc'),
          },
          customData: { content_ids: [campaignId] },
        })
      }

      await seedingPublicApi.applyToCampaign(mallId, shopNo, campaignId, {
        ...formData,
        phone: stripPhone(formData.phone),
      })

      localStorage.removeItem('seeding_prefill')
      onSuccess()
    } catch (err: any) {
      setError(err.message || '신청 중 오류가 발생했어요.')
    } finally {
      setIsApplying(false)
    }
  }

  // ==================== 렌더링 ====================

  return (
    <form onSubmit={handleApply}>
      {/* 기본 정보 */}
      <div className={`grid grid-cols-1 ${forceMobile ? '' : 'md:grid-cols-2'} gap-4 mb-8`}>
        {/* 이름 */}
        <div data-field="name">
          <label className="block text-sm font-medium text-on-surface mb-1.5">
            이름 <span className="text-error">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, name: e.target.value }))
              clearFieldError('name')
            }}
            className={`w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent ${fieldErrors.name ? 'border-error' : 'border-border'}`}
          />
          {fieldErrors.name && <p className="text-error text-xs mt-1">{fieldErrors.name}</p>}
        </div>

        {/* 전화번호 */}
        <div data-field="phone">
          <label className="block text-sm font-medium text-on-surface mb-1.5">
            전화번호 <span className="text-error">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, phone: formatPhoneInput(e.target.value, prev.phone) }))
                clearFieldError('phone')
                if (phoneVerified) {
                  setPhoneVerified(false)
                  setCodeSent(false)
                  setVerificationCode('')
                }
              }}
              placeholder="010-1234-5678"
              className={`flex-1 px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent ${fieldErrors.phone ? 'border-error' : 'border-border'}`}
              readOnly={phoneVerified}
            />
            {!phoneVerified ? (
              <button
                type="button"
                onClick={handleSendCode}
                disabled={previewMode || isSendingCode || !formData.phone}
                className="px-3 py-2.5 bg-on-surface text-white text-sm rounded-lg hover:bg-on-surface/90 disabled:bg-outline-variant disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSendingCode
                  ? '발송 중...'
                  : codeSent
                    ? '재발송'
                    : '인증코드 발송'}
              </button>
            ) : (
              <span className="flex items-center px-3 py-2.5 text-green-600 font-medium text-sm whitespace-nowrap">
                ✓ 인증완료
              </span>
            )}
          </div>
          {/* 인증코드 입력 */}
          {codeSent && !phoneVerified && (
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(
                    e.target.value.replace(/\D/g, '').slice(0, 6),
                  )
                }
                placeholder="6자리 인증코드"
                maxLength={6}
                className="flex-1 px-4 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleVerifyCode}
                disabled={previewMode || isVerifyingCode || verificationCode.length !== 6}
                className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-dim disabled:bg-outline-variant disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isVerifyingCode ? '확인 중...' : '확인'}
              </button>
            </div>
          )}
          {(verifyError || fieldErrors.phone) && (
            <p className="text-error text-xs mt-1">{verifyError || fieldErrors.phone}</p>
          )}
        </div>

        {/* 이메일 (field_config 기반 조건부 표시) */}
        {campaign.field_config?.email !== 'hidden' && (
          <div className={forceMobile ? '' : 'md:col-span-2'} data-field="email">
            <label className="block text-sm font-medium text-on-surface mb-1.5">
              이메일 {campaign.field_config?.email === 'required' ? <span className="text-error">*</span> : <span className="text-outline-variant text-xs font-normal">(선택)</span>}
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, email: e.target.value }))
                clearFieldError('email')
              }}
              className={`w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent ${fieldErrors.email ? 'border-error' : 'border-border'}`}
            />
            {fieldErrors.email && <p className="text-error text-xs mt-1">{fieldErrors.email}</p>}
          </div>
        )}

        {/* 성별 (field_config 기반 조건부 표시) */}
        {campaign.field_config?.gender !== 'hidden' && (
          <div className={forceMobile ? '' : 'md:col-span-2'} data-field="gender">
            <label className="block text-sm font-medium text-on-surface mb-1.5">
              성별 {campaign.field_config?.gender === 'required' ? <span className="text-error">*</span> : <span className="text-outline-variant text-xs font-normal">(선택)</span>}
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer py-1">
                <input
                  type="radio"
                  name="gender"
                  value="male"
                  checked={formData.gender === 'male'}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, gender: e.target.value }))
                    clearFieldError('gender')
                  }}
                  className="w-4 h-4 text-primary"
                />
                <span className="text-sm">남성</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer py-1">
                <input
                  type="radio"
                  name="gender"
                  value="female"
                  checked={formData.gender === 'female'}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, gender: e.target.value }))
                    clearFieldError('gender')
                  }}
                  className="w-4 h-4 text-primary"
                />
                <span className="text-sm">여성</span>
              </label>
            </div>
            {fieldErrors.gender && <p className="text-error text-xs mt-1">{fieldErrors.gender}</p>}
          </div>
        )}

        {/* 생년월일 — 오버레이 플레이스홀더 (field_config 기반 조건부 표시) */}
        {campaign.field_config?.birthdate !== 'hidden' && (() => {
          const fullText = formatBirthdateWithTemplate(birthdateDisplay)
          const typedLen = getBirthdateTypedLength(birthdateDisplay)
          return (
            <div className={forceMobile ? '' : 'md:col-span-2'} data-field="birthdate">
              <label className="block text-sm font-medium text-on-surface mb-1.5">
                생년월일 {campaign.field_config?.birthdate === 'required' ? <span className="text-error">*</span> : <span className="text-outline-variant text-xs font-normal">(선택)</span>}
              </label>
              <div className="relative">
                <div
                  aria-hidden
                  className="absolute inset-0 flex items-center px-4 py-2 pointer-events-none text-sm"
                >
                  <span className="text-on-surface">{fullText.slice(0, typedLen)}</span>
                  <span className="text-outline-variant">{fullText.slice(typedLen)}</span>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={fullText.slice(0, typedLen)}
                  onChange={(e) => handleBirthdateChange(e.target.value)}
                  onBlur={handleBirthdateBlur}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent text-sm text-transparent caret-on-surface bg-transparent relative z-10 ${birthdateError || fieldErrors.birthdate ? 'border-error' : 'border-border'}`}
                />
              </div>
              {(birthdateError || fieldErrors.birthdate) && (
                <p className="text-error text-xs mt-1">{birthdateError || fieldErrors.birthdate}</p>
              )}
            </div>
          )
        })()}
      </div>

      {/* 배송지 (field_config 기반 조건부 표시) — Kakao 우편번호 검색 연동 */}
      {campaign.field_config?.address !== 'hidden' && (
        <div className="mb-8 pt-6 border-t border-border" data-field="address">
          <h3 className="text-base font-semibold text-on-surface mb-3">
            배송지 정보 {campaign.field_config?.address === 'required' ? <span className="text-error">*</span> : <span className="text-outline-variant text-xs font-normal">(선택)</span>}
          </h3>
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.zipcode}
                readOnly
                placeholder="우편번호"
                className={`w-28 px-4 py-2 border rounded-lg text-sm bg-surface cursor-default ${fieldErrors.address ? 'border-error' : 'border-border'}`}
              />
              <button
                type="button"
                onClick={() => setShowPostcodeModal(true)}
                className="px-4 py-2 bg-on-surface text-white text-sm rounded-lg hover:bg-on-surface/90 whitespace-nowrap"
              >
                주소 찾기
              </button>
            </div>
            <input
              type="text"
              value={formData.address1}
              readOnly
              placeholder="기본 주소"
              className={`w-full px-4 py-2 border rounded-lg text-sm bg-surface cursor-default ${fieldErrors.address ? 'border-error' : 'border-border'}`}
            />
            <input
              ref={address2Ref}
              type="text"
              value={formData.address2}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, address2: e.target.value }))
              }
              placeholder="상세 주소 (동/호수 등)"
              className="w-full px-4 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent"
            />
          </div>
          {fieldErrors.address && <p className="text-error text-xs mt-1">{fieldErrors.address}</p>}
        </div>
      )}

      {/* SNS 계정 (social_media_channels에 채널이 있을 때) */}
      {campaign.social_media_channels &&
        Object.keys(campaign.social_media_channels).length > 0 && (
        <div className="mb-8 pt-6 border-t border-border">
          <h3 className="text-base font-semibold text-on-surface mb-3">
            SNS 정보
          </h3>
          <div className="space-y-4">
            {Object.entries(campaign.social_media_channels).map(([ch, visibility]) => {
              const label = channelLabels[ch] || { name: ch, accountLabel: '계정', followersLabel: '팔로워' }
              const data = getSnsData(ch)
              return (
                <div key={ch} data-field={`sns_${ch}`}>
                  <label className="block text-sm font-medium text-on-surface mb-1.5">
                    {label.name} {visibility === 'required' ? <span className="text-error">*</span> : <span className="text-outline-variant text-xs font-normal">(선택)</span>}
                  </label>
                  <div className={`grid grid-cols-1 ${forceMobile ? '' : 'md:grid-cols-2'} gap-2`}>
                    {['instagram', 'tiktok', 'youtube'].includes(ch) ? (
                      <div className={`flex rounded-lg border focus-within:ring-2 focus-within:ring-ring focus-within:border-transparent ${snsErrors[ch] ? 'border-error' : 'border-border'}`}>
                        <span className="inline-flex items-center px-3 text-sm text-on-surface-variant bg-surface border-r border-border rounded-l-lg">@</span>
                        <input
                          type="text"
                          value={data.account || ''}
                          onChange={(e) => {
                            updateSnsAccount(ch, 'account', e.target.value)
                            if (snsErrors[ch]) setSnsErrors((prev) => { const next = { ...prev }; delete next[ch]; return next })
                            clearFieldError(`sns_${ch}`)
                          }}
                          onBlur={() => handleSnsBlur(ch)}
                          placeholder={SNS_CHANNELS[ch]?.placeholder?.replace('@', '') || `${label.accountLabel} 입력`}
                          className="flex-1 px-4 py-2 text-sm rounded-r-lg focus:outline-none"
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={data.account || ''}
                        onChange={(e) => {
                          updateSnsAccount(ch, 'account', e.target.value)
                          if (snsErrors[ch]) setSnsErrors((prev) => { const next = { ...prev }; delete next[ch]; return next })
                          clearFieldError(`sns_${ch}`)
                        }}
                        onBlur={() => handleSnsBlur(ch)}
                        placeholder={SNS_CHANNELS[ch]?.placeholder || `${label.accountLabel} 입력`}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent text-sm ${snsErrors[ch] ? 'border-error' : 'border-border'}`}
                      />
                    )}
                    {/* 팔로워 선택 — 공개 신청 폼 고유 스타일 유지 (Select 컴포넌트 미적용: 대시보드 디자인 시스템과 별개) */}
                    <div className="relative">
                      <select
                        value={data.followers || ''}
                        onChange={(e) => updateSnsAccount(ch, 'followers', e.target.value)}
                        className="appearance-none w-full px-4 py-2 pr-10 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
                      >
                        {(followerRangesByChannel[ch] || followerRangesByChannel.instagram).map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.value ? `${label.followersLabel}: ${r.label}` : `${label.followersLabel} 선택`}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-outline-variant pointer-events-none" />
                    </div>
                  </div>
                  {snsErrors[ch] && (
                    <p className="text-error text-xs mt-1">{snsErrors[ch]}</p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* 신청 사유 + 추가 질문 — 하나의 섹션으로 통합 */}
      {(campaign.field_config?.reason !== 'hidden' || (campaign.extra_questions && campaign.extra_questions.length > 0)) && (
        <div className="mb-8 pt-6 border-t border-border">
          {campaign.field_config?.reason !== 'hidden' && (
            <div className="pb-4" data-field="reason">
              <label className="block text-sm font-medium text-on-surface mb-1.5">
                신청 사유 {campaign.field_config?.reason === 'required' ? <span className="text-error">*</span> : <span className="text-outline-variant text-xs font-normal">(선택)</span>}
              </label>
              <textarea
                value={formData.application_reason}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    application_reason: e.target.value,
                  }))
                  clearFieldError('reason')
                }}
                rows={3}
                placeholder="왜 이 체험단에 참여하고 싶으신가요?"
                className={`w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent ${fieldErrors.reason ? 'border-error' : 'border-border'}`}
              />
              {fieldErrors.reason && <p className="text-error text-xs mt-1">{fieldErrors.reason}</p>}
            </div>
          )}

          {/* 추가 질문 */}
          {[...(campaign.extra_questions || [])]
            .sort((a, b) => a.order - b.order)
            .map((q) => {
              const errorKey = `custom_${q.id}`
              const answer = (formData.extra_question_answers || []).find(a => a.question_id === q.id)
              const updateAnswer = (value: string | string[]) => {
                setFormData(prev => {
                  const existing = prev.extra_question_answers || []
                  const idx = existing.findIndex(a => a.question_id === q.id)
                  const newAnswers = [...existing]
                  if (idx >= 0) {
                    newAnswers[idx] = { question_id: q.id, value }
                  } else {
                    newAnswers.push({ question_id: q.id, value })
                  }
                  return { ...prev, extra_question_answers: newAnswers }
                })
                setFieldErrors(prev => {
                  const next = { ...prev }
                  delete next[errorKey]
                  return next
                })
              }

              return (
                <div key={q.id} className="py-4 border-t border-border first:border-t-0 first:pt-0" data-field={errorKey}>
                  <label className="block text-sm font-medium text-on-surface mb-1.5">
                    {q.label} {q.required ? <span className="text-error">*</span> : <span className="text-outline-variant text-xs font-normal">(선택)</span>}
                  </label>

                  {q.type === 'text' && (
                    <input
                      type="text"
                      value={(answer?.value as string) || ''}
                      onChange={e => updateAnswer(e.target.value)}
                      placeholder="답변을 입력해주세요"
                      className={`w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent ${fieldErrors[errorKey] ? 'border-error' : 'border-border'}`}
                    />
                  )}

                  {q.type === 'textarea' && (
                    <textarea
                      value={(answer?.value as string) || ''}
                      onChange={e => updateAnswer(e.target.value)}
                      rows={3}
                      placeholder="답변을 입력해주세요"
                      className={`w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent ${fieldErrors[errorKey] ? 'border-error' : 'border-border'}`}
                    />
                  )}

                  {q.type === 'select' && (
                    <div className="space-y-2 mt-1">
                      {(q.options || []).map((opt, optIdx) => (
                        <label key={optIdx} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`extra_q_${q.id}`}
                            checked={(answer?.value as string) === opt}
                            onChange={() => updateAnswer(opt)}
                            className="border-border text-primary focus:ring-ring"
                          />
                          <span className="text-sm text-on-surface">{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {q.type === 'checkbox' && (
                    <div className="space-y-2 mt-1">
                      {(q.options || []).map((opt, optIdx) => {
                        const currentValues = (Array.isArray(answer?.value) ? answer.value : []) as string[]
                        const checked = currentValues.includes(opt)
                        return (
                          <label key={optIdx} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const next = checked
                                  ? currentValues.filter(v => v !== opt)
                                  : [...currentValues, opt]
                                updateAnswer(next)
                              }}
                              className="rounded border-border"
                            />
                            <span className="text-sm text-on-surface">{opt}</span>
                          </label>
                        )
                      })}
                    </div>
                  )}

                  {fieldErrors[errorKey] && (
                    <p className="text-error text-xs mt-1">{fieldErrors[errorKey]}</p>
                  )}
                </div>
              )
            })}
        </div>
      )}

      {/* 안내사항 */}
      {campaign.notices && (
        <div className="mb-8 border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-surface border-b border-border">
            <h3 className="text-base font-semibold text-on-surface">안내사항</h3>
          </div>
          <div className="px-4 py-3 text-sm text-on-surface leading-relaxed whitespace-pre-line">
            {campaign.notices}
          </div>
        </div>
      )}

      {/* 개인정보 동의 */}
      <div className="mb-8 space-y-4">
        {/* 개인정보 수집·이용 동의 */}
        <div data-field="agreed_privacy" className={`border rounded-lg overflow-hidden ${fieldErrors.agreed_privacy ? 'border-error' : 'border-border'}`}>
          <div className="flex items-center justify-between px-3 py-2.5 bg-surface">
            <label className="flex items-center gap-2 cursor-pointer flex-1">
              <input
                type="checkbox"
                checked={formData.agreed_privacy}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    agreed_privacy: e.target.checked,
                  }))
                  if (e.target.checked) clearFieldError('agreed_privacy')
                }}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
              />
              <span className="text-sm text-on-surface">
                <span className="font-medium text-error">[필수]</span>{' '}
                개인정보 수집·이용 동의
              </span>
            </label>
            <button
              type="button"
              onClick={() => setShowPrivacyDetail((v) => !v)}
              className="text-xs text-on-surface-variant underline hover:text-on-surface ml-2 shrink-0"
            >
              {showPrivacyDetail ? '접기' : '자세히 보기'}
            </button>
          </div>
          {showPrivacyDetail && (
            <div className="px-4 py-3 text-xs text-on-surface-variant leading-relaxed border-t border-border bg-surface-lowest space-y-2">
              {/* 개인정보 수집 테이블 — 공개 신청 폼 법적 고지 (Table 컴포넌트 미적용: 초소형 법적 텍스트 레이아웃) */}
              <table className="w-full border-collapse text-xs">
                <tbody>
                  <tr className="border-b border-border">
                    <td className="py-1.5 pr-3 font-medium text-on-surface whitespace-nowrap align-top w-20">수집 항목</td>
                    <td className="py-1.5">
                      이름, 전화번호
                      {campaign.field_config?.email !== 'hidden' && ', 이메일'}
                      {campaign.field_config?.gender !== 'hidden' && ', 성별'}
                      {campaign.field_config?.birthdate !== 'hidden' && ', 생년월일'}
                      {campaign.field_config?.address !== 'hidden' && ', 주소(우편번호, 기본주소, 상세주소)'}
                      {campaign.social_media_channels && Object.keys(campaign.social_media_channels).length > 0 && ', SNS 계정 정보'}
                    </td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-1.5 pr-3 font-medium text-on-surface whitespace-nowrap align-top w-20">수집 목적</td>
                    <td className="py-1.5">체험단 신청 접수, 선정 결과 안내, 체험 상품 배송, 미션 작성 확인 및 리워드 지급</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-1.5 pr-3 font-medium text-on-surface whitespace-nowrap align-top w-20">보유 기간</td>
                    <td className="py-1.5">캠페인 종료 후 6개월 (이후 지체 없이 파기)</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-on-surface-variant">※ 동의를 거부할 수 있으나, 거부 시 체험단 신청이 불가합니다.</p>
            </div>
          )}
        </div>
        {fieldErrors.agreed_privacy && <p className="text-error text-xs -mt-2">{fieldErrors.agreed_privacy}</p>}

        {/* 개인정보 제3자 제공 동의 */}
        <div data-field="agreed_third_party" className={`border rounded-lg overflow-hidden ${fieldErrors.agreed_third_party ? 'border-error' : 'border-border'}`}>
          <div className="flex items-center justify-between px-3 py-2.5 bg-surface">
            <label className="flex items-center gap-2 cursor-pointer flex-1">
              <input
                type="checkbox"
                checked={formData.agreed_third_party}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    agreed_third_party: e.target.checked,
                  }))
                  if (e.target.checked) clearFieldError('agreed_third_party')
                }}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
              />
              <span className="text-sm text-on-surface">
                <span className="font-medium text-error">[필수]</span>{' '}
                개인정보 제3자 제공 동의
              </span>
            </label>
            <button
              type="button"
              onClick={() => setShowThirdPartyDetail((v) => !v)}
              className="text-xs text-on-surface-variant underline hover:text-on-surface ml-2 shrink-0"
            >
              {showThirdPartyDetail ? '접기' : '자세히 보기'}
            </button>
          </div>
          {showThirdPartyDetail && (
            <div className="px-4 py-3 text-xs text-on-surface-variant leading-relaxed border-t border-border bg-surface-lowest space-y-2">
              {/* 제3자 제공 테이블 — 공개 신청 폼 법적 고지 (Table 컴포넌트 미적용: 초소형 법적 텍스트 레이아웃) */}
              <table className="w-full border-collapse text-xs">
                <tbody>
                  <tr className="border-b border-border">
                    <td className="py-1.5 pr-3 font-medium text-on-surface whitespace-nowrap align-top w-20">제공받는 자</td>
                    <td className="py-1.5">쇼핑몰 운영사 ({campaign?.shop_name || '해당 쇼핑몰'})</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-1.5 pr-3 font-medium text-on-surface whitespace-nowrap align-top w-20">제공 항목</td>
                    <td className="py-1.5">
                      이름, 전화번호
                      {campaign.field_config?.email !== 'hidden' && ', 이메일'}
                      {campaign.field_config?.gender !== 'hidden' && ', 성별'}
                      {campaign.field_config?.birthdate !== 'hidden' && ', 생년월일'}
                      {campaign.field_config?.address !== 'hidden' && ', 주소'}
                      {campaign.social_media_channels && Object.keys(campaign.social_media_channels).length > 0 && ', SNS 계정 정보'}
                    </td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-1.5 pr-3 font-medium text-on-surface whitespace-nowrap align-top w-20">제공 목적</td>
                    <td className="py-1.5">체험 상품 배송 및 미션 작성 확인</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-1.5 pr-3 font-medium text-on-surface whitespace-nowrap align-top w-20">보유 기간</td>
                    <td className="py-1.5">제공 목적 달성 후 6개월</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-on-surface-variant">※ 동의를 거부할 수 있으나, 거부 시 체험단 신청이 불가합니다.</p>
            </div>
          )}
        </div>
        {fieldErrors.agreed_third_party && <p className="text-error text-xs -mt-2">{fieldErrors.agreed_third_party}</p>}
      </div>

      {/* API 에러 (서버 응답 에러만 표시) */}
      {error && (
        <div className="mb-4 p-4 bg-error/10 border border-error/30 rounded-lg text-error text-sm">
          {error}
        </div>
      )}

      {/* 제출 — 비활성 이유 안내 */}
      {!previewMode && !phoneVerified && (
        <p className="text-xs text-on-surface-variant text-center mb-2">
          📱 위에서 전화번호 인증을 완료하면 신청할 수 있어요
        </p>
      )}
      <button
        type="submit"
        disabled={previewMode || isApplying || !phoneVerified}
        className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary-dim disabled:bg-outline-variant disabled:cursor-not-allowed font-medium"
      >
        {previewMode
          ? '미리보기 모드'
          : isApplying
            ? '신청 중...'
            : !phoneVerified
              ? '전화번호 인증 후 신청 가능'
              : '신청하기'}
      </button>

      {/* Kakao 우편번호 검색 모달 */}
      {showPostcodeModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
          onClick={() => setShowPostcodeModal(false)}
        >
          <div
            className="bg-surface-lowest rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h3 className="text-base font-semibold text-on-surface">주소 찾기</h3>
              <button
                type="button"
                onClick={() => setShowPostcodeModal(false)}
                className="text-outline-variant hover:text-on-surface-variant"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div ref={postcodeEmbedRef} className="h-[min(450px,70vh)]" />
          </div>
        </div>
      )}
    </form>
  )
}
